import { ESCALA } from "../tema";
import { opcoesBase } from "./base";

/**
 * Família 8 — comparativo em seta/funil.
 *
 * "Falta Vender x Já Vendido", "Média Prevista x Vendas Confirmadas": dois
 * valores, uma seta larga que se estreita entre eles e a proporção em
 * porcentagem. Não é um gráfico estatístico — é um desenho.
 *
 * Mesmo assim continua sendo um chart do Highcharts, com `series: []` e os
 * eixos invisíveis. O motivo é prático: assim ele herda de graça o
 * cabeçalho de três linhas, a tipografia, o tamanho fixo e principalmente
 * o MESMO pipeline de exportação das outras sete famílias. Um SVG à mão
 * exigiria um segundo caminho de rasterização, um segundo preview e uma
 * segunda implementação do cabeçalho — que divergiriam na primeira vez que
 * alguém mexesse no estilo da casa.
 */

const modelo = {
  id: "funil-comparativo",
  label: "Comparativo em seta",
  descricao: "Dois valores e a proporção entre eles. Ex.: Falta Vender x Já Vendido.",

  build(spec) {
    const base = opcoesBase(spec);
    const funil = spec.extras?.funil || {};

    // Variante pizza 3D: mesma informação (a proporção entre dois
    // valores), mas no formato de rosca em perspectiva que aparece nas
    // referências de vendas de exportação. Aqui a fatia é série de
    // verdade; só as caixas de texto são desenho.
    if (funil.formato === "pizza3d") {
      const esq = funil.esquerda || {};
      const dir = funil.direita || {};
      const parte = Math.max(0.01, Math.min(0.99, funil.divisao ?? 0.5));
      return {
        ...base,
        chart: {
          ...base.chart,
          type: "pie",
          marginBottom: 14 * ESCALA,
          options3d: { enabled: true, alpha: 64, beta: 0 },
        },
        xAxis: { ...base.xAxis, visible: false },
        yAxis: { ...base.yAxis, visible: false },
        legend: { ...base.legend, enabled: false },
        plotOptions: {
          ...base.plotOptions,
          pie: {
            depth: 34 * ESCALA,
            innerSize: funil.rosca ? "45%" : 0,
            dataLabels: { enabled: false },
            borderWidth: 0,
            startAngle: funil.anguloInicial ?? 0,
            size: "125%",
          },
        },
        series: [
          {
            name: "Proporção",
            data: [
              { name: esq.rotulo || "A", y: parte * 100, color: esq.cor || "#8B0000" },
              {
                name: dir.rotulo || "B",
                y: (1 - parte) * 100,
                color: dir.cor || "#00B050",
                // Fatia destacada, como na referência.
                sliced: true,
              },
            ],
          },
        ],
      };
    }

    return {
      ...base,
      chart: {
        ...base.chart,
        type: "line",
        marginBottom: 12 * ESCALA,
        marginLeft: 6 * ESCALA,
        marginRight: 6 * ESCALA,
      },
      // Sem série e sem eixo: tudo aqui é desenho, mas o cabeçalho, a
      // tipografia e a exportação continuam vindo da mesma base.
      xAxis: { ...base.xAxis, visible: false },
      yAxis: { ...base.yAxis, visible: false },
      legend: { ...base.legend, enabled: false },
      series: [],
    };
  },

  exemplo() {
    return {
      modelo: "funil-comparativo",
      titulo: [
        { texto: "Milho", cor: "#FFFFFF" },
        { texto: " - Vendas Exportação ", cor: "#FFFFFF" },
        { texto: "EUA", cor: "#F0F000" },
        { texto: " (27/ago/2026)", cor: "#F0F000", tamanho: 8 },
      ],
      subtitulo: [{ texto: "Safra 2026/27 - Projeção: 83,2 milhões/t", cor: "#F0F000" }],
      fonte: "Fonte: RURAL BUSINESS/USDA",
      fundo: "#000000",
      fundoPlot: "nenhum",
      dados: { categorias: [], series: [] },
      legenda: { visivel: false },
      valores: { decimais: 1, prefixo: "", sufixo: "", exibirEm: "nenhum" },
      rotulo: { corTexto: "#FFFFFF", corFundo: "#1A1A1A", corBorda: "#FFFFFF" },
      extras: {
        funil: {
          // "pizza3d" (rosca em perspectiva) ou "seta" (bloco que afina
          // até a ponta) — as duas formas aparecem nas referências.
          formato: "pizza3d",
          rosca: true,
          divisao: 0.83,
          esquerda: {
            rotulo: "Falta Vender",
            valor: "68,7 milhões/t",
            percentual: "83%",
            cor: "#8B0000",
            corCaixa: "#000000",
            corBorda: "#FFFFFF",
            corTexto: "#FFFFFF",
          },
          direita: {
            rotulo: "Já Vendido",
            valor: "14,5 milhões/t",
            percentual: "17%",
            cor: "#00B050",
            corCaixa: "#000000",
            corBorda: "#F0F000",
            corTexto: "#F0F000",
          },
        },
      },
    };
  },
};

export default modelo;
